import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, DownloadOutlined, LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDate } from "../shared/money";
import Amount from "../shared/Amount";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import { accountSheet, errorText } from "./api";
import {
  ORIGIN_COLORS,
  ORIGIN_LABELS,
  TYPE_COLORS,
  TYPE_LABELS,
  csvMoney,
  downloadCsv,
  formatBalance
} from "./labels";

/** Kontoblatt: every line on one account with the running balance. */
const AccountSheet = ({ accounts }) => {
  const { t } = useTranslation("accounting");
  const { number } = useParams();
  const history = useHistory();
  const [range, setRange] = useState([dayjs().startOf("year"), dayjs().endOf("year")]);
  const [account, setAccount] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range?.[0]) params.from = range[0].format("YYYY-MM-DD");
      if (range?.[1]) params.to = range[1].format("YYYY-MM-DD");
      const res = await accountSheet(number, params);
      setAccount(res.data.account);
      setRows(res.data.rows || []);
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not load the account sheet")));
    } finally {
      setLoading(false);
    }
  }, [number, range, t]);

  useEffect(() => {
    load();
  }, [load]);

  const totalDebit = rows.reduce((s, r) => s + r.debit, 0);
  const totalCredit = rows.reduce((s, r) => s + r.credit, 0);
  const closing = rows.length ? rows[rows.length - 1].balance : 0;

  const exportCsv = () =>
    downloadCsv(
      `Kontoblatt_${number}.csv`,
      [
        t("Date"),
        t("No."),
        t("Document no. (Belegnummer)"),
        t("Text"),
        t("Contra accounts"),
        t("Debit (Soll)"),
        t("Credit (Haben)"),
        t("Balance"),
        t("Origin")
      ],
      rows.map(r => [
        formatDate(r.date),
        `${r.entryNo}/${r.year}`,
        r.belegRef,
        r.text,
        r.contraAccounts.join(" "),
        csvMoney(r.debit),
        csvMoney(r.credit),
        csvMoney(r.balance),
        r.origin
      ])
    );

  const columns = [
    { title: t("Date"), dataIndex: "date", width: 100, render: formatDate },
    {
      title: t("No."),
      key: "no",
      width: 90,
      render: (_, r) => (
        <span className="acct-num">{`${r.entryNo}/${String(r.year).slice(-2)}`}</span>
      )
    },
    { title: t("Document no. (Belegnummer)"), dataIndex: "belegRef", width: 150, ellipsis: true },
    { title: t("Text"), dataIndex: "text", ellipsis: true },
    {
      title: t("Contra accounts"),
      dataIndex: "contraAccounts",
      width: 200,
      render: list => (
        <Space size={4} wrap>
          {list.map(n => (
            <Tooltip
              key={n}
              title={accounts.byNumber.get(n)?.name || ""}
              trigger={["hover", "focus"]}
            >
              <Typography.Link
                onClick={() => history.push(`/user/accounting/journal/accounts/${n}`)}
              >
                {n}
              </Typography.Link>
            </Tooltip>
          ))}
        </Space>
      )
    },
    {
      title: t("Debit (Soll)"),
      dataIndex: "debit",
      align: "end",
      width: 120,
      render: v => (v ? <Amount cents={v} /> : "")
    },
    {
      title: t("Credit (Haben)"),
      dataIndex: "credit",
      align: "end",
      width: 120,
      render: v => (v ? <Amount cents={v} /> : "")
    },
    {
      title: t("Balance"),
      dataIndex: "balance",
      align: "end",
      width: 140,
      render: v => <span className="acct-num">{formatBalance(v)}</span>
    },
    {
      title: t("Origin"),
      dataIndex: "origin",
      width: 110,
      render: o => <Tag color={ORIGIN_COLORS[o]}>{t(ORIGIN_LABELS[o] || o)}</Tag>
    },
    { key: "lock", width: 40, render: (_, r) => (r.locked ? <LockOutlined /> : null) }
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          marginBottom: 12
        }}
      >
        <Typography.Title level={5} className="acct-wrap" style={{ margin: 0 }}>
          {t("Account sheet (Kontoblatt)")} {number} {account?.name ? `· ${account.name}` : ""}
        </Typography.Title>
        {account ? (
          <Tag color={TYPE_COLORS[account.type]}>
            {t(TYPE_LABELS[account.type] || account.type)}
          </Tag>
        ) : null}
      </div>
      <PageToolbar
        filters={
          <DatePicker.RangePicker
            value={range}
            onChange={setRange}
            format="DD.MM.YYYY"
            allowClear={false}
          />
        }
        actions={
          <>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/user/accounting/journal/accounts")}
            >
              {t("All accounts")}
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!rows.length}>
              {t("Export CSV")}
            </Button>
          </>
        }
      />
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        rowKey={(r, i) => `${r.entry}-${i}`}
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 100, showSizeChanger: true }}
        scroll={{ x: 1100 }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>{t("Totals")}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="end">
                <Amount cents={totalDebit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="end">
                <Amount cents={totalCredit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="end">
                <span className="acct-num acct-num--strong">{formatBalance(closing)}</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} colSpan={2} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default AccountSheet;
